import { useEffect, useMemo, useState } from "react";
import { list, fileUrl, updateRecord, deleteRecord, createRecord } from "../lib/pb";
import { navigate } from "../App";
import { useT, type T } from "../lib/i18n";
import type { Article, ArticleStatus, ArticleKind } from "../lib/types";

const STATUSES: Array<ArticleStatus | "all"> = [
  "all", "draft", "review", "scheduled", "published", "archived",
];
const KINDS: Array<ArticleKind | "all"> = ["all", "article", "video", "visual_essay"];

type View = "grid" | "list";

export default function ArticlesPage() {
  const t = useT();
  const [items, setItems] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<ArticleStatus | "all">("all");
  const [filterKind, setFilterKind] = useState<ArticleKind | "all">("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>(() => {
    try { return (localStorage.getItem("reverium-articles-view") as View) || "list"; }
    catch { return "list"; }
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const filter: string[] = [];
      if (filterStatus !== "all") filter.push(`status='${filterStatus}'`);
      if (filterKind !== "all") filter.push(`kind='${filterKind}'`);
      if (search) {
        const safe = search.replace(/'/g, "");
        filter.push(`(title ~ '${safe}' || summary ~ '${safe}')`);
      }
      const r = await list<Article>("articles", {
        perPage: 100,
        sort: "-updated",
        filter: filter.length ? filter.join(" && ") : undefined,
        expand: "author,tags",
      });
      setItems(r.items);
      setSelected(new Set());
    } catch (e) {
      alert(`Failed to load articles: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterKind]);

  function setViewPersisted(v: View) {
    setView(v);
    try { localStorage.setItem("reverium-articles-view", v); } catch { /* ignore */ }
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function createNew() {
    try {
      const created = await createRecord<Article>("articles", {
        title: "Untitled article",
        slug: `draft-${Date.now()}`,
        kind: "article",
        summary: "",
        status: "draft",
        is_premium: false,
        featured: false,
      } as Record<string, unknown>);
      navigate(`/article/${created.id}`);
    } catch (e) {
      // Fallback to draft creation route — server may require author.
      navigate("/article/new");
      void e;
    }
  }

  async function bulkAction(action: "publish" | "archive" | "delete") {
    if (selected.size === 0) return;
    const verb = action === "delete" ? "delete" : action;
    if (!confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${selected.size} article(s)?`)) return;
    setBulkBusy(true);
    try {
      for (const id of selected) {
        if (action === "delete") {
          await deleteRecord("articles", id);
        } else {
          await updateRecord("articles", id, {
            status: action === "publish" ? "published" : "archived",
          });
        }
      }
      await load();
    } catch (e) {
      alert(`Bulk action failed: ${(e as Error).message}`);
    } finally {
      setBulkBusy(false);
    }
  }

  const allOnPageSelected = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id)),
    [items, selected],
  );
  function toggleAll() {
    if (allOnPageSelected) setSelected(new Set());
    else setSelected(new Set(items.map((it) => it.id)));
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.articlesTitle}</h1>
        <p className="page-subtitle">{t.articlesSubtitle(items.length)}</p>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <div style={{ display: "flex", gap: 0, border: "1px solid var(--border-1)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
            <button onClick={() => setViewPersisted("list")} className="btn btn-ghost btn-sm" style={{ borderRadius: 0, background: view === "list" ? "var(--bg-2)" : "transparent", color: view === "list" ? "var(--text-0)" : "var(--text-3)" }}>{t.articlesViewList}</button>
            <button onClick={() => setViewPersisted("grid")} className="btn btn-ghost btn-sm" style={{ borderRadius: 0, background: view === "grid" ? "var(--bg-2)" : "transparent", color: view === "grid" ? "var(--text-0)" : "var(--text-3)" }}>{t.articlesViewGrid}</button>
          </div>
          <button onClick={createNew} className="btn btn-primary">{t.articlesNew}</button>
        </div>
      </div>

      {/* Filters bar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`pill ${filterStatus === s ? "active" : ""}`}>
              {s === "all" ? t.mediaFilterAll : statusLabel(t, s)}
            </button>
          ))}
        </div>
        <div style={{ width: 12 }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {KINDS.map((k) => (
            <button key={k} onClick={() => setFilterKind(k)} className={`pill ${filterKind === k ? "active" : ""}`}>
              {k === "all" ? t.mediaFilterAll : kindLabel(t, k)}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder={t.articlesSearchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void load(); }}
          className="input"
          style={{ marginLeft: "auto", width: 240 }}
        />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px",
            background: "var(--bg-1)",
            border: "1px solid var(--border-2)",
            borderRadius: "var(--radius-md)",
            marginBottom: 14,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text-1)" }}>
            {t.articlesSelectedN(selected.size)}
          </span>
          <button onClick={() => setSelected(new Set())} className="btn btn-ghost btn-sm">{t.articlesBulkClear}</button>
          <div style={{ flex: 1 }} />
          <button disabled={bulkBusy} onClick={() => bulkAction("publish")} className="btn btn-secondary btn-sm">{t.articlesBulkPublish}</button>
          <button disabled={bulkBusy} onClick={() => bulkAction("archive")} className="btn btn-secondary btn-sm">{t.articlesBulkArchive}</button>
          <button disabled={bulkBusy} onClick={() => bulkAction("delete")} className="btn btn-danger btn-sm">{t.articlesBulkDelete}</button>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton-card skeleton" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">▦</span>
          <div className="empty-title">{t.articlesNothingFound}</div>
          <div className="empty-subtitle">{t.articlesSubtitle(0)}</div>
          <button onClick={createNew} className="btn btn-primary" style={{ marginTop: 6 }}>{t.articlesNew}</button>
        </div>
      ) : view === "list" ? (
        <ListView items={items} selected={selected} onToggle={toggleSelected} allSelected={allOnPageSelected} onToggleAll={toggleAll} />
      ) : (
        <GridView items={items} selected={selected} onToggle={toggleSelected} />
      )}
    </div>
  );
}

export function statusLabel(t: T, s: ArticleStatus): string {
  switch (s) {
    case "draft": return t.statusDraft;
    case "review": return t.statusReview;
    case "scheduled": return t.statusScheduled;
    case "published": return t.statusPublished;
    case "archived": return t.statusArchived;
  }
}

export function kindLabel(t: T, k: ArticleKind): string {
  switch (k) {
    case "video": return t.kindVideo;
    case "article": return t.kindArticle;
    case "visual_essay": return t.kindVisualEssay;
  }
}

function ListView({
  items, selected, onToggle, allSelected, onToggleAll,
}: {
  items: Article[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  allSelected: boolean;
  onToggleAll: () => void;
}) {
  const t = useT();
  return (
    <table className="table">
      <thead>
        <tr>
          <th style={{ width: 32 }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onToggleAll}
              style={{ cursor: "pointer", accentColor: "var(--accent)" }}
            />
          </th>
          <th style={{ width: 56 }}></th>
          <th>{t.articlesColTitle}</th>
          <th style={{ width: 130 }}>{t.articlesColAuthor}</th>
          <th style={{ width: 110 }}>{t.articlesFilterStatus}</th>
          <th style={{ width: 100 }}>{t.articlesFilterKind}</th>
          <th style={{ width: 140 }}>{t.articlesColUpdated}</th>
          <th style={{ width: 70 }}>{t.articlesColViews}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((a) => (
          <tr key={a.id} onClick={(e) => {
            const tag = (e.target as HTMLElement).tagName;
            if (tag === "INPUT") return;
            navigate(`/article/${a.id}`);
          }}>
            <td onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selected.has(a.id)}
                onChange={() => onToggle(a.id)}
                style={{ cursor: "pointer", accentColor: "var(--accent)" }}
              />
            </td>
            <td>
              {a.cover ? (
                <img
                  src={fileUrl("articles", a.id, a.cover, "96x54")}
                  alt=""
                  style={{ width: 56, height: 32, objectFit: "cover", borderRadius: 4, display: "block" }}
                />
              ) : (
                <div style={{ width: 56, height: 32, background: "var(--bg-3)", borderRadius: 4 }} />
              )}
            </td>
            <td>
              <div style={{ fontWeight: 500, color: "var(--text-0)" }}>{a.title}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2, display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {a.summary}
              </div>
            </td>
            <td style={{ color: "var(--text-1)" }}>{a.expand?.author?.name || "—"}</td>
            <td><StatusBadge status={a.status} /></td>
            <td style={{ color: "var(--text-2)" }}>{kindLabel(t, a.kind)}</td>
            <td style={{ color: "var(--text-2)" }}>{formatDate(a.updated)}</td>
            <td style={{ color: "var(--text-2)" }}>{a.view_count || 0}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GridView({
  items, selected, onToggle,
}: {
  items: Article[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  const t = useT();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
      {items.map((a) => (
        <div
          key={a.id}
          className="card card-hover"
          style={{ overflow: "hidden", cursor: "pointer", position: "relative" }}
          onClick={(e) => {
            const tag = (e.target as HTMLElement).tagName;
            if (tag === "INPUT") return;
            navigate(`/article/${a.id}`);
          }}
        >
          <label
            style={{ position: "absolute", top: 8, left: 8, zIndex: 2, display: "flex", alignItems: "center", padding: 4, background: "rgba(28,28,30,0.7)", borderRadius: 4, cursor: "pointer" }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={selected.has(a.id)}
              onChange={() => onToggle(a.id)}
              style={{ cursor: "pointer", accentColor: "var(--accent)" }}
            />
          </label>
          <div className="card-cover" style={{ aspectRatio: "16/9" }}>
            {a.cover ? (
              <img
                src={fileUrl("articles", a.id, a.cover, "480x270")}
                alt=""
              />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-4)", fontSize: 26 }}>
                ▢
              </div>
            )}
          </div>
          <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <StatusBadge status={a.status} />
              <span style={{ fontSize: 10, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>{kindLabel(t, a.kind)}</span>
            </div>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 17, fontWeight: 600, color: "var(--text-0)", lineHeight: 1.3, letterSpacing: "-0.01em", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {a.title}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {a.summary}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-3)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
              <span>{a.expand?.author?.name || "—"}</span>
              <span>{formatDate(a.updated)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: ArticleStatus }) {
  const t = useT();
  return <span className={`badge badge-${status}`}>{statusLabel(t, status)}</span>;
}

export function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
